import { useMemo } from 'react';
import useSWR from 'swr';

import { fetcher } from 'utils/axios';

export type NotificationFeedItem = {
  id: string;
  category: 'live' | 'crash' | 'ota' | 'legacy-ota' | 'command' | 'battery' | 'knowledge';
  severity: 'info' | 'success' | 'warning' | 'error';
  title: string;
  summary: string;
  tenant: string | null;
  serialNumber: string | null;
  occurredAt: string;
  route: string;
  sourceLabel: string;
  titleKey?: string;
  titleValues?: Record<string, string>;
  summaryKey?: string;
  summaryValues?: Record<string, string>;
  sourceLabelKey?: string;
};

export type NotificationFeedSummary = {
  generatedAt: string;
  unreadCount: number;
  items: NotificationFeedItem[];
};

type NotificationFeedResponse = {
  item: NotificationFeedSummary;
};

const endpoint = '/notifications';
const streamEndpoint = '/notifications/stream';
const NOTIFICATION_FEED_REFRESH_MS = 30000;

function shouldRetryNotificationRequest(error: unknown): boolean {
  const status = typeof error === 'object' && error !== null ? (error as { status?: number }).status : undefined;
  return typeof status === 'number' && status < 500;
}

export function useGetNotificationFeed() {
  const { data, isLoading, error, isValidating } = useSWR<NotificationFeedResponse>(endpoint, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    refreshInterval: NOTIFICATION_FEED_REFRESH_MS,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    shouldRetryOnError: shouldRetryNotificationRequest,
    errorRetryCount: 1,
    errorRetryInterval: 15000,
    dedupingInterval: 5000
  });

  return useMemo(
    () => ({
      notificationFeed: data?.item ?? null,
      notificationFeedLoading: isLoading,
      notificationFeedError: error,
      notificationFeedValidating: isValidating
    }),
    [data, error, isLoading, isValidating]
  );
}

export function getNotificationFeedKey() {
  return endpoint;
}

export function getNotificationFeedStreamPath() {
  return streamEndpoint;
}
